import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Eye, EyeOff } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'

// Setting keys for Stripe (platform-wide, always test keys)
const STRIPE_SETTINGS = {
  PUBLISHABLE_KEY: 'stripe_publishable_key',
  SECRET_KEY: 'stripe_secret_key',
  WEBHOOK_SECRET: 'stripe_webhook_secret',
} as const

// Setting keys for Google Ads
const GOOGLE_ADS_SETTINGS = {
  API_KEY: 'google_ads_api_key',
} as const

// Setting keys for LogRocket
const LOGROCKET_SETTINGS = {
  APP_ID: 'logrocket_app_id',
  ENABLED: 'logrocket_enabled',
} as const

const stripeSettingsSchema = z.object({
  stripe_publishable_key: z.string().optional(),
  stripe_secret_key: z.string().optional(),
  stripe_webhook_secret: z.string().optional(),
})

const googleAdsSettingsSchema = z.object({
  google_ads_api_key: z.string().optional(),
})

const logrocketSettingsSchema = z.object({
  logrocket_enabled: z.boolean().default(false),
  logrocket_app_id: z.string().optional(),
})

type StripeSettingsFormData = z.infer<typeof stripeSettingsSchema>
type GoogleAdsSettingsFormData = z.infer<typeof googleAdsSettingsSchema>
type LogRocketSettingsFormData = z.infer<typeof logrocketSettingsSchema>
export function AdminSettingsPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [showSecretKey, setShowSecretKey] = useState(false)
  const [showWebhookSecret, setShowWebhookSecret] = useState(false)
  const [showGoogleAdsKey, setShowGoogleAdsKey] = useState(false)

  // Fetch platform settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['platform-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('*')

      if (error) throw error

      // Convert to a key-value map
      const settingsMap: Record<string, string> = {}
      data?.forEach((setting) => {
        settingsMap[setting.key] = setting.value || ''
      })
      return settingsMap
    },
  })

  const stripeForm = useForm<StripeSettingsFormData>({
    resolver: zodResolver(stripeSettingsSchema),
    defaultValues: {
      stripe_publishable_key: '',
      stripe_secret_key: '',
      stripe_webhook_secret: '',
    },
  })

  const googleAdsForm = useForm<GoogleAdsSettingsFormData>({
    resolver: zodResolver(googleAdsSettingsSchema),
    defaultValues: {
      google_ads_api_key: '',
    },
  })

  const logrocketForm = useForm<LogRocketSettingsFormData>({
    resolver: zodResolver(logrocketSettingsSchema),
    defaultValues: {
      logrocket_enabled: false,
      logrocket_app_id: '',
    },
  })

  // Update forms when settings are loaded
  useEffect(() => {
    if (settings) {
      stripeForm.reset({
        stripe_publishable_key: settings[STRIPE_SETTINGS.PUBLISHABLE_KEY] || '',
        stripe_secret_key: settings[STRIPE_SETTINGS.SECRET_KEY] || '',
        stripe_webhook_secret: settings[STRIPE_SETTINGS.WEBHOOK_SECRET] || '',
      })
      googleAdsForm.reset({
        google_ads_api_key: settings[GOOGLE_ADS_SETTINGS.API_KEY] || '',
      })
      logrocketForm.reset({
        logrocket_enabled: settings[LOGROCKET_SETTINGS.ENABLED] === 'true',
        logrocket_app_id: settings[LOGROCKET_SETTINGS.APP_ID] || '',
      })
    }
  }, [settings, stripeForm, googleAdsForm, logrocketForm])

  const stripeMutation = useMutation({
    mutationFn: async (data: StripeSettingsFormData) => {
      const settingsToUpsert = [
        {
          key: STRIPE_SETTINGS.PUBLISHABLE_KEY,
          value: data.stripe_publishable_key || null,
          description: 'Stripe Publishable Key',
          is_secret: false,
        },
        {
          key: STRIPE_SETTINGS.SECRET_KEY,
          value: data.stripe_secret_key || null,
          description: 'Stripe Secret Key',
          is_secret: true,
        },
        {
          key: STRIPE_SETTINGS.WEBHOOK_SECRET,
          value: data.stripe_webhook_secret || null,
          description: 'Stripe Webhook Signing Secret',
          is_secret: true,
        },
      ]

      // Upsert each setting
      for (const setting of settingsToUpsert) {
        const { error } = await supabase
          .from('platform_settings')
          .upsert(setting, { onConflict: 'key' })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-settings'] })
      toast({
        title: 'Settings saved',
        description: 'Your Stripe settings have been updated.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const googleAdsMutation = useMutation({
    mutationFn: async (data: GoogleAdsSettingsFormData) => {
      const { error } = await supabase
        .from('platform_settings')
        .upsert(
          {
            key: GOOGLE_ADS_SETTINGS.API_KEY,
            value: data.google_ads_api_key || null,
            description: 'Google Ads API Key',
            is_secret: true,
          },
          { onConflict: 'key' }
        )

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-settings'] })
      toast({
        title: 'Settings saved',
        description: 'Your Google Ads settings have been updated.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const logrocketMutation = useMutation({
    mutationFn: async (data: LogRocketSettingsFormData) => {
      const settingsToUpsert = [
        {
          key: LOGROCKET_SETTINGS.ENABLED,
          value: data.logrocket_enabled ? 'true' : 'false',
          description: 'LogRocket Integration Enabled',
          is_secret: false,
        },
        {
          key: LOGROCKET_SETTINGS.APP_ID,
          value: data.logrocket_app_id || null,
          description: 'LogRocket App ID',
          is_secret: false,
        },
      ]

      // Upsert each setting
      for (const setting of settingsToUpsert) {
        const { error } = await supabase
          .from('platform_settings')
          .upsert(setting, { onConflict: 'key' })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-settings'] })
      toast({
        title: 'Settings saved',
        description: 'Your LogRocket settings have been updated.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })


  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground">
            Manage platform-wide settings
          </p>
        </div>
        <div className="max-w-2xl">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-64" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-24" />
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">
          Manage platform-wide settings
        </p>
      </div>

      <div className="grid gap-6 max-w-2xl">
        {/* Stripe Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Stripe</CardTitle>
            <CardDescription>
              Platform Stripe test credentials. These keys are used when accounts enable Test Mode in their integration settings.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...stripeForm}>
              <form
                onSubmit={stripeForm.handleSubmit((data) => stripeMutation.mutate(data))}
                className="space-y-4"
              >
                <FormField
                  control={stripeForm.control}
                  name="stripe_publishable_key"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Publishable Key</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="pk_test_..."
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Stripe publishable key (starts with pk_test_)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={stripeForm.control}
                  name="stripe_secret_key"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Secret Key</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showSecretKey ? 'text' : 'password'}
                            placeholder="sk_test_..."
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowSecretKey(!showSecretKey)}
                          >
                            {showSecretKey ? (
                              <EyeOff className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Eye className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormDescription>
                        Stripe secret key (starts with sk_test_)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={stripeForm.control}
                  name="stripe_webhook_secret"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Webhook Secret</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showWebhookSecret ? 'text' : 'password'}
                            placeholder="whsec_..."
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                          >
                            {showWebhookSecret ? (
                              <EyeOff className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Eye className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormDescription>
                        Webhook signing secret (starts with whsec_)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" disabled={stripeMutation.isPending}>
                  {stripeMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Save Changes
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Google Ads Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Google Ads</CardTitle>
            <CardDescription>
              Configure your Google Ads API integration
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...googleAdsForm}>
              <form
                onSubmit={googleAdsForm.handleSubmit((data) => googleAdsMutation.mutate(data))}
                className="space-y-4"
              >
                <FormField
                  control={googleAdsForm.control}
                  name="google_ads_api_key"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>API Key</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showGoogleAdsKey ? 'text' : 'password'}
                            placeholder="Enter your Google Ads API key"
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                            onClick={() => setShowGoogleAdsKey(!showGoogleAdsKey)}
                          >
                            {showGoogleAdsKey ? (
                              <EyeOff className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Eye className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormDescription>
                        Your Google Ads API key for conversion tracking
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" disabled={googleAdsMutation.isPending}>
                  {googleAdsMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Save Changes
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* LogRocket Settings */}
        <Card>
          <CardHeader>
            <CardTitle>LogRocket</CardTitle>
            <CardDescription>
              Session replay and error tracking for all viewer websites
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...logrocketForm}>
              <form
                onSubmit={logrocketForm.handleSubmit((data) => logrocketMutation.mutate(data))}
                className="space-y-4"
              >
                <FormField
                  control={logrocketForm.control}
                  name="logrocket_enabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Enable Integration</FormLabel>
                        <FormDescription>
                          Load LogRocket on all viewer websites for session replay
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={logrocketForm.control}
                  name="logrocket_app_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>App ID</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="org-slug/app-slug"
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Your LogRocket App ID from dashboard Settings &gt; General Settings
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" disabled={logrocketMutation.isPending}>
                  {logrocketMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Save Changes
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

      </div>
    </div>
  )
}
